class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    const addToCartModule = document.querySelector(".wt-product__add-to-cart");
    const btn = this.querySelector("button");
    const addToCart = this.dataset.addToCart === "";

    const forObserver = document.querySelectorAll(
      ".wt-product__add-to-cart, .wt-footer, .wt-product__name",
    );

    let intersected = [];

    btn.addEventListener("click", (e) => {
      if (!addToCart) {
        e.preventDefault();
      }
      addToCartModule.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        isIntersecting
          ? intersected.push(target)
          : (intersected = intersected.filter((item) => item !== target));
      });

      if (intersected.length) {
        this.classList.remove(this.activeClass);
      } else {
        this.classList.add(this.activeClass);
      }
    });

    forObserver.forEach((item) => {
      observer.observe(item);
    });
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

class MobileStickyATC extends HTMLElement {
  constructor() {
    super();
    this.activeClass = "cb-pdp__mobile-sticky-atc--show";
  }

  connectedCallback() {
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    if (this.observer) {
      this.observer.disconnect();
    }
    // Clean up body class and padding
    document.body.classList.remove("cb-mobile-sticky-atc-visible");
    document.body.style.paddingBottom = "";
  }

  handleResize() {
    // Only initialize on mobile
    if (window.innerWidth >= 750) {
      this.classList.remove(this.activeClass);
      document.body.classList.remove("cb-mobile-sticky-atc-visible");
      document.body.style.paddingBottom = ""; // Remove padding on desktop
      const root = document.getElementById("root");
      if (root) root.classList.remove("cb-mobile-sticky-atc-visible");
      const productSection = document.querySelector(".wt-product__wrapper");
      if (productSection) productSection.classList.remove("cb-mobile-sticky-atc-visible");
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
        this.initialized = false;
      }
      return;
    }
    if (!this.initialized) {
      this.initialize();
      this.initialized = true;
    } else {
      // Recalculate padding on resize if panel is visible
      if (this.classList.contains(this.activeClass)) {
        const panelHeight = this.offsetHeight;
        if (panelHeight > 0) {
          document.body.style.paddingBottom = `${panelHeight}px`;
        }
      }
    }
  }

  initialize() {
    const buyBox = document.querySelector(".wt-product__add-to-cart");
    if (!buyBox) {
      console.warn("MobileStickyATC: Buy box not found");
      return;
    }

    const btn = this.querySelector("button");
    const addToCart = this.dataset.addToCart === "";

    // Update price on load and when variant changes
    this.updatePrice();
    
    // Also update after a short delay to ensure price is rendered
    setTimeout(() => this.updatePrice(), 100);
    setTimeout(() => this.updatePrice(), 500);

    // Click handler - scroll to buy box if not direct add to cart
    if (btn) {
      btn.addEventListener("click", (e) => {
        const mainSubmitBtn = document.querySelector(
          ".wt-product__add-to-cart .product-form__submit",
        );

        // Always route through the main product form button so validation/errors behave the same.
        if (mainSubmitBtn) {
          e.preventDefault();
          // If main button is disabled (e.g., no variant chosen), just scroll to it
          if (mainSubmitBtn.disabled || mainSubmitBtn.getAttribute("aria-disabled") === "true") {
            buyBox.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            return;
          }
          mainSubmitBtn.click();
          return;
        }

        // Fallback: just scroll to the form (prevents double navigation on type=submit)
        e.preventDefault();
        buyBox.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }

    // Function to update body padding based on sticky panel height
    const updateBodyPadding = () => {
      if (this.classList.contains(this.activeClass)) {
        // Panel is visible, measure its height and apply padding
        const panelHeight = this.offsetHeight;
        if (panelHeight > 0) {
          document.body.style.paddingBottom = `${panelHeight}px`;
        }
      } else {
        // Panel is hidden, remove padding
        document.body.style.paddingBottom = "";
      }
    };

    // Observe when buy box goes out of view
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const rect = entry.boundingClientRect;
          const isScrolledPast = !entry.isIntersecting && rect.top < 0;
          
          if (isScrolledPast) {
            // Buy box is scrolled past (above viewport) - show sticky bar
            this.classList.add(this.activeClass);
            document.body.classList.add("cb-mobile-sticky-atc-visible");
            const root = document.getElementById("root");
            if (root) root.classList.add("cb-mobile-sticky-atc-visible");
            const productSection = document.querySelector(".wt-product__wrapper");
            if (productSection) productSection.classList.add("cb-mobile-sticky-atc-visible");
            
            // Update body padding after panel is shown (use requestAnimationFrame to ensure DOM is updated)
            requestAnimationFrame(() => {
              updateBodyPadding();
            });
          } else {
            // Buy box is visible or not yet scrolled to - hide sticky bar
            this.classList.remove(this.activeClass);
            document.body.classList.remove("cb-mobile-sticky-atc-visible");
            const root = document.getElementById("root");
            if (root) root.classList.remove("cb-mobile-sticky-atc-visible");
            const productSection = document.querySelector(".wt-product__wrapper");
            if (productSection) productSection.classList.remove("cb-mobile-sticky-atc-visible");
            
            // Remove body padding when panel is hidden
            updateBodyPadding();
          }
        });
      },
      {
        root: null,
        rootMargin: "-1px 0px 0px 0px", // Trigger when buy box is 1px above viewport
        threshold: 0,
      }
    );

    observer.observe(buyBox);
    this.observer = observer; // Store reference for cleanup

    // Listen for variant changes to update price
    document.addEventListener("variant:change", () => {
      this.updatePrice();
    });
  }

  updatePrice() {
    const priceContainer = this.querySelector("[data-price-container]");
    if (!priceContainer) return;

    // Find the main price element and clone its content
    const mainPriceElement = document.querySelector(".wt-product__price .price");
    if (mainPriceElement) {
      const priceClone = mainPriceElement.cloneNode(true);
      priceContainer.innerHTML = "";
      priceContainer.appendChild(priceClone);
    }
  }
}

customElements.define("mobile-sticky-atc", MobileStickyATC);

class DesktopStickyATC extends HTMLElement {
  constructor() {
    super();
    this.activeClass = "cb-pdp__desktop-sticky-atc--show";
  }

  connectedCallback() {
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    if (this.observer) this.observer.disconnect();
  }

  handleResize() {
    // Only on desktop (750px+)
    if (window.innerWidth < 750) {
      this.classList.remove(this.activeClass);
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
        this.initialized = false;
      }
      return;
    }
    if (!this.initialized) {
      this.initialize();
      this.initialized = true;
    }
  }

  initialize() {
    const buyBox = document.querySelector(".wt-product__add-to-cart");
    if (!buyBox) return;

    const btn = this.querySelector("button");

    if (btn) {
      btn.addEventListener("click", (e) => {
        const mainSubmitBtn = document.querySelector(
          ".wt-product__add-to-cart .product-form__submit",
        );
        if (mainSubmitBtn) {
          e.preventDefault();
          if (mainSubmitBtn.disabled || mainSubmitBtn.getAttribute("aria-disabled") === "true") {
            buyBox.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
          mainSubmitBtn.click();
          return;
        }
        e.preventDefault();
        buyBox.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isScrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          if (isScrolledPast) {
            this.classList.add(this.activeClass);
          } else {
            this.classList.remove(this.activeClass);
          }
        });
      },
      { root: null, rootMargin: "-1px 0px 0px 0px", threshold: 0 },
    );

    this.observer.observe(buyBox);

    // Update price on variant change
    document.addEventListener("variant:change", () => this.updatePrice());
  }

  updatePrice() {
    const priceContainer = this.querySelector("[data-price-container]");
    if (!priceContainer) return;
    const mainPrice = document.querySelector(".wt-product__price .price .price-item--regular, .wt-product__price .price .price-item--sale");
    if (mainPrice) {
      priceContainer.textContent = mainPrice.textContent;
    }
  }
}

customElements.define("desktop-sticky-atc", DesktopStickyATC);

// Description truncation toggle (mobile)
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".cb-pdp__desc-truncate__toggle").forEach((btn) => {
    btn.addEventListener("click", () => {
      const wrapper = btn.closest(".cb-pdp__desc-truncate");
      const expanded = wrapper.classList.toggle("is-expanded");
      btn.setAttribute("aria-expanded", expanded);
      btn.querySelector(".cb-pdp__desc-truncate__toggle-text").textContent = expanded ? "Less" : "More";
      const chevron = btn.querySelector("svg");
      if (chevron) chevron.style.transform = expanded ? "rotate(180deg)" : "";
    });
  });
});

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}

// Gifting Layer functionality
class GiftingLayer extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId || '';
    this.occasionInput = null;
    this.recipientInput = null;
    this.messageInput = null;
    this.hidePricesInput = null;
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    // Get hidden inputs
    this.occasionInput = document.getElementById(`gifting-occasion-${this.sectionId}`);
    this.recipientInput = document.getElementById(`gifting-recipient-${this.sectionId}`);
    this.messageInput = document.getElementById(`gifting-message-${this.sectionId}`);
    this.hidePricesInput = document.getElementById(`gifting-hide-prices-${this.sectionId}`);

    // Initialize chips
    this.initChips();
    
    // Initialize gift message
    this.initGiftMessage();
    
    // Initialize hide prices checkbox
    this.initHidePrices();
  }

  initChips() {
    const occasionChips = this.querySelectorAll('[data-occasion]');
    const recipientChips = this.querySelectorAll('[data-recipient]');

    // Occasion chips
    occasionChips.forEach(chip => {
      chip.addEventListener('click', () => {
        // Remove active state from all occasion chips
        occasionChips.forEach(c => {
          c.classList.remove('cb-pdp__gifting-layer__chip--active');
          c.setAttribute('aria-pressed', 'false');
        });
        
        // Add active state to clicked chip
        chip.classList.add('cb-pdp__gifting-layer__chip--active');
        chip.setAttribute('aria-pressed', 'true');
        
        // Update hidden input
        if (this.occasionInput) {
          this.occasionInput.value = chip.dataset.occasion;
        }
      });
    });

    // Recipient chips
    recipientChips.forEach(chip => {
      chip.addEventListener('click', () => {
        // Remove active state from all recipient chips
        recipientChips.forEach(c => {
          c.classList.remove('cb-pdp__gifting-layer__chip--active');
          c.setAttribute('aria-pressed', 'false');
        });
        
        // Add active state to clicked chip
        chip.classList.add('cb-pdp__gifting-layer__chip--active');
        chip.setAttribute('aria-pressed', 'true');
        
        // Update hidden input
        if (this.recipientInput) {
          this.recipientInput.value = chip.dataset.recipient;
        }
      });
    });
  }

  initGiftMessage() {
    const textarea = this.querySelector('.cb-pdp__gifting-layer__gift-message-textarea');
    const preview = document.getElementById(`gift-message-preview-${this.sectionId}`);

    if (textarea && preview && this.messageInput) {
      textarea.addEventListener('input', (e) => {
        const message = e.target.value;
        
        // Update preview
        if (message.trim()) {
          preview.textContent = message;
        } else {
          preview.textContent = 'Your message will appear here...';
        }
        
        // Update hidden input
        this.messageInput.value = message;
      });
    }
  }

  initHidePrices() {
    const checkbox = this.querySelector('.cb-pdp__gifting-layer__hide-prices-checkbox');
    
    if (checkbox && this.hidePricesInput) {
      checkbox.addEventListener('change', (e) => {
        this.hidePricesInput.value = e.target.checked ? 'Yes' : '';
      });
    }
  }

  openAccordion() {
    // Accordion removed - just focus on textarea
    const textarea = this.querySelector('.cb-pdp__gifting-layer__gift-message-textarea');

    if (textarea) {
      setTimeout(() => {
        textarea.focus();
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }
}

customElements.define('gifting-layer', GiftingLayer);

// Initialize "Send as a Gift" button functionality
function initSendAsGiftButton() {
  const sendAsGiftButtons = document.querySelectorAll('.product-form__send-as-gift');
  
  sendAsGiftButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      
      // Find the gifting layer trigger
      const trigger = document.querySelector('.cb-pdp__gifting-layer__trigger.wt-collapse__trigger');
      
      if (trigger) {
        // Scroll to trigger
        trigger.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Click trigger to open accordion
        setTimeout(() => {
          if (trigger.getAttribute('aria-expanded') === 'false') {
            trigger.click();
          }
          
          // Focus on textarea after accordion opens
          setTimeout(() => {
            const textarea = document.querySelector('.cb-pdp__gifting-layer__gift-message-textarea');
            if (textarea) {
              textarea.focus();
            }
          }, 400);
        }, 100);
      }
    });
  });
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSendAsGiftButton);
} else {
  initSendAsGiftButton();
}

// ============================================
// SOCIAL PROOF - UGC Strip Carousel/Scroll
// ============================================
function initUGCStrip() {
  // UGC strip now uses native CSS scrolling with drag support
  const ugcContainers = document.querySelectorAll('[data-ugc-scroll]');
  
  ugcContainers.forEach(container => {
    // Remove any Swiper classes if they exist
    container.classList.remove('swiper');
    const wrapper = container.querySelector('.swiper-wrapper');
    if (wrapper) {
      // Move slides back to container
      const slides = Array.from(wrapper.children);
      slides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        container.appendChild(slide);
      });
      wrapper.remove();
    }
    
    // Add drag-to-scroll functionality for desktop
    let isDown = false;
    let startX;
    let scrollLeft;
    
    // Prevent image dragging
    const items = container.querySelectorAll('.cb-pdp__social-proof__ugc-strip__item');
    items.forEach(item => {
      item.addEventListener('dragstart', (e) => {
        e.preventDefault();
        return false;
      });
      item.style.pointerEvents = 'auto';
    });
    
    container.addEventListener('mousedown', (e) => {
      // Only start drag if clicking on container background, not on images
      if (e.target === container || e.target.closest('.cb-pdp__social-proof__ugc-strip__item')) {
        // Allow dragging from anywhere in the container
        isDown = true;
        container.classList.add('active');
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        e.preventDefault();
      }
    });
    
    container.addEventListener('mouseleave', () => {
      isDown = false;
      container.classList.remove('active');
    });
    
    container.addEventListener('mouseup', () => {
      isDown = false;
      container.classList.remove('active');
    });
    
    container.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      container.scrollLeft = scrollLeft - walk;
    });
  });
}

// ============================================
// SOCIAL PROOF - Review Filter Chips
// ============================================
function initReviewFilterChips() {
  const filterChips = document.querySelectorAll('.cb-pdp__social-proof__review-filters__chip');
  const reviewCards = document.querySelectorAll('.cb-pdp__social-proof__review-highlights__card');
  const noReviewsMessage = document.querySelector('.cb-pdp__social-proof__no-reviews');
  
  if (filterChips.length === 0 || reviewCards.length === 0) return;
  
  filterChips.forEach(chip => {
    chip.addEventListener('click', (e) => {
      e.preventDefault();
      const selectedCategory = chip.getAttribute('data-filter');
      
      // Remove active class from all chips
      filterChips.forEach(c => c.classList.remove('active'));
      
      // Add active class to clicked chip
      chip.classList.add('active');
      
      // Filter review cards and count visible ones
      let visibleCount = 0;
      reviewCards.forEach(card => {
        const cardCategory = card.getAttribute('data-category');
        
        if (selectedCategory === 'all' || cardCategory === 'all' || cardCategory === selectedCategory) {
          // Show card
          card.style.display = '';
          visibleCount++;
        } else {
          // Hide card
          card.style.display = 'none';
        }
      });
      
      // Show/hide "No reviews yet" message
      if (noReviewsMessage) {
        if (visibleCount === 0) {
          noReviewsMessage.style.display = 'block';
        } else {
          noReviewsMessage.style.display = 'none';
        }
      }
    });
  });
}

// Initialize social proof features
function initSocialProof() {
  initUGCStrip();
  initReviewFilterChips();
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSocialProof);
} else {
  initSocialProof();
}

// ============================================
// AOV Expansion - AJAX Add to Cart
// ============================================

function initAOVExpansion() {
  const addOnButtons = document.querySelectorAll('[data-add-to-cart]');
  
  addOnButtons.forEach(button => {
    button.addEventListener('click', async function(e) {
      e.preventDefault();
      const card = button.closest('.cb-pdp__aov-expansion__add-on-card');
      if (!card) return;
      
      const variantId = card.getAttribute('data-variant-id');
      const productId = card.getAttribute('data-product-id');
      
      if (!variantId) {
        console.warn('No variant ID found for add-on product');
        return;
      }
      
      const originalText = button.textContent;
      
      button.disabled = true;
      button.classList.add('adding');
      button.textContent = 'Adding...';
      
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        
        const addResponse = await fetch(window.Shopify?.routes?.root + 'cart/add.js', {
          method: 'POST',
          body: formData
        });
        
        const cartData = await addResponse.json();
        
        if (addResponse.ok && !cartData.status) {
          button.classList.remove('adding');
          button.classList.add('added');
          button.textContent = 'Added';
          
          // Publish cart update event using theme's PUB_SUB system
          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'aov-expansion',
              productVariantId: variantId,
              cartData: cartData
            });
          }
          
          // Open cart drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            if (!cartDrawer.isOpen) {
              cartDrawer.toggleDrawerClasses();
            }
            document.dispatchEvent(new CustomEvent('cart-drawer:refresh', {
              detail: { response: cartData },
              bubbles: true
            }));
          }
          
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
            button.classList.remove('added');
          }, 2000);
        } else {
          throw new Error(cartData.description || 'Failed to add to cart');
        }
      } catch (error) {
        console.error('AOV Expansion add to cart error:', error);
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('adding');
        alert('Unable to add product to cart. Please try again.');
      }
    });
  });
}

// Initialize AOV Expansion on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAOVExpansion);
} else {
  initAOVExpansion();
}

// What's Inside Column Slider
function initWhatsInsideSliders() {
  // Don't initialize slider on screens smaller than 480px
  if (window.innerWidth < 480) return;
  
  const sliderWrappers = document.querySelectorAll('[data-slider-wrapper]');
  
  sliderWrappers.forEach(wrapper => {
    const slider = wrapper.querySelector('[data-slider]');
    const items = slider.querySelectorAll('[data-slider-item]');
    const dots = wrapper.querySelectorAll('[data-slider-dot]');
    
    if (items.length <= 3) return; // No slider needed if 3 or fewer items
    
    let currentPage = 0;
    const itemsPerPage = 3;
    const totalPages = Math.ceil(items.length / itemsPerPage);
    let isAnimating = false;
    
    // Drag/Swipe variables
    let isDragging = false;
    let startX = 0;
    let currentX = 0;
    let dragDistance = 0;
    const minDragDistance = 50; // Minimum pixels to trigger slide change
    
    function showPage(pageIndex, direction = 'next') {
      if (isAnimating) return;
      
      // Clamp page index
      pageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));
      
      const oldPage = currentPage;
      if (oldPage === pageIndex) return;
      
      isAnimating = true;
      currentPage = pageIndex;
      
      // Get old and new items
      const oldStartIndex = oldPage * itemsPerPage;
      const oldEndIndex = Math.min(oldStartIndex + itemsPerPage, items.length);
      const newStartIndex = currentPage * itemsPerPage;
      const newEndIndex = Math.min(newStartIndex + itemsPerPage, items.length);
      
      // Determine animation direction
      const isForward = currentPage > oldPage || direction === 'next';
      const outClass = isForward ? 'slide-out-left' : 'slide-out-right';
      const inClass = isForward ? 'slide-in-right' : 'slide-in-left';
      
      // Animate out old items
      for (let i = oldStartIndex; i < oldEndIndex; i++) {
        items[i].classList.add(outClass);
      }
      
      // After animation, hide old items and show new ones
      setTimeout(() => {
        // Hide and clean up old items
        for (let i = oldStartIndex; i < oldEndIndex; i++) {
          items[i].classList.add('hidden');
          items[i].classList.remove(outClass, inClass);
        }
        
        // Show and animate in new items
        for (let i = newStartIndex; i < newEndIndex; i++) {
          items[i].classList.remove('hidden');
          items[i].classList.add(inClass);
        }
        
        // Clean up animation classes after animation completes
        setTimeout(() => {
          for (let i = newStartIndex; i < newEndIndex; i++) {
            items[i].classList.remove(inClass);
          }
          isAnimating = false;
        }, 400);
      }, 400);
      
      // Update dots immediately
      dots.forEach((dot, index) => {
        if (index === currentPage) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    }
    
    // Mouse/Touch Start
    function handleDragStart(e) {
      isDragging = true;
      startX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
      slider.style.cursor = 'grabbing';
    }
    
    // Mouse/Touch Move
    function handleDragMove(e) {
      if (!isDragging) return;
      e.preventDefault();
      currentX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
      dragDistance = currentX - startX;
    }
    
    // Mouse/Touch End
    function handleDragEnd() {
      if (!isDragging) return;
      isDragging = false;
      slider.style.cursor = 'grab';
      
      // Check if drag distance is enough to change page
      if (Math.abs(dragDistance) > minDragDistance) {
        if (dragDistance > 0) {
          // Swiped right - go to previous page
          showPage(currentPage - 1, 'prev');
        } else {
          // Swiped left - go to next page
          showPage(currentPage + 1, 'next');
        }
      }
      
      dragDistance = 0;
    }
    
    // Initialize first page (no animation on initial load)
    items.forEach((item, index) => {
      if (index >= itemsPerPage) {
        item.classList.add('hidden');
      }
    });
    
    // Set first dot as active
    if (dots.length > 0) {
      dots[0].classList.add('active');
    }
    
    // Add grab cursor
    slider.style.cursor = 'grab';
    slider.style.userSelect = 'none';
    
    // Add click handlers to dots
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const direction = index > currentPage ? 'next' : 'prev';
        showPage(index, direction);
      });
    });
    
    // Add drag/swipe handlers
    // Mouse events
    slider.addEventListener('mousedown', handleDragStart);
    slider.addEventListener('mousemove', handleDragMove);
    slider.addEventListener('mouseup', handleDragEnd);
    slider.addEventListener('mouseleave', handleDragEnd);
    
    // Touch events
    slider.addEventListener('touchstart', handleDragStart, { passive: false });
    slider.addEventListener('touchmove', handleDragMove, { passive: false });
    slider.addEventListener('touchend', handleDragEnd);
  });
}

// Initialize What's Inside Sliders on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWhatsInsideSliders);
} else {
  initWhatsInsideSliders();
}

// Re-initialize on window resize (with debounce)
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    // Clear all hidden classes when resizing
    document.querySelectorAll('[data-slider-item].hidden').forEach(item => {
      item.classList.remove('hidden');
    });
    // Re-initialize sliders
    initWhatsInsideSliders();
  }, 250);
});

// Image Columns Show More/Less functionality
function initImageColumnsToggle() {
  const showMoreBtn = document.querySelector('[data-show-more-images]');
  if (!showMoreBtn) return;

  const imageColumnsContainer = document.querySelector('.cb-pdp__whats-inside__image-columns');
  const showText = showMoreBtn.querySelector('[data-show-text]');
  const hideText = showMoreBtn.querySelector('[data-hide-text]');

  if (!imageColumnsContainer || !showText || !hideText) return;

  // Check if button should be visible based on screen size and column count
  function updateButtonVisibility() {
    const screenWidth = window.innerWidth;
    const totalColumns = imageColumnsContainer.querySelectorAll('.cb-pdp__whats-inside__image-column').length;
    let visibleCount = 3; // Desktop default (750px+)
    
    if (screenWidth < 750 && screenWidth >= 480) {
      visibleCount = 2; // Tablet (480px - 749px)
    } else if (screenWidth < 480) {
      visibleCount = 1; // Mobile (<480px)
    }
    
    if (totalColumns <= visibleCount) {
      showMoreBtn.style.display = 'none';
    } else {
      showMoreBtn.style.display = 'block';
    }
  }

  // Initial check
  updateButtonVisibility();

  // Update on resize
  let buttonResizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(buttonResizeTimer);
    buttonResizeTimer = setTimeout(updateButtonVisibility, 250);
  });

  showMoreBtn.addEventListener('click', () => {
    const isExpanded = imageColumnsContainer.classList.contains('expanded');

    if (isExpanded) {
      // Determine which columns to hide based on screen size
      const screenWidth = window.innerWidth;
      let selector = '.cb-pdp__whats-inside__image-column:nth-child(n+4)'; // Desktop (750px+): hide 4+
      
      if (screenWidth < 750 && screenWidth >= 480) {
        selector = '.cb-pdp__whats-inside__image-column:nth-child(n+3)'; // Tablet (480px-749px): hide 3+
      } else if (screenWidth < 480) {
        selector = '.cb-pdp__whats-inside__image-column:nth-child(n+2)'; // Mobile (<480px): hide 2+
      }
      
      const columnsToHide = imageColumnsContainer.querySelectorAll(selector);
      
      columnsToHide.forEach((col, index) => {
        col.style.animation = 'fadeOutScale 0.3s ease forwards';
        col.style.animationDelay = `${index * 0.05}s`;
      });

      // Wait for animation to complete before collapsing
      setTimeout(() => {
        imageColumnsContainer.classList.remove('expanded');
        showText.style.display = '';
        hideText.style.display = 'none';
        
        // Reset animation styles
        columnsToHide.forEach(col => {
          col.style.animation = '';
          col.style.animationDelay = '';
        });
      }, 300 + (columnsToHide.length * 50));
    } else {
      // Expand - show all
      imageColumnsContainer.classList.add('expanded');
      showText.style.display = 'none';
      hideText.style.display = '';
    }
  });
}

// Initialize Image Columns Toggle on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initImageColumnsToggle);
} else {
  initImageColumnsToggle();
}
